import React, { createContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ApiService } from "../components/ApiService";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const authCode = queryParams.get("authCode");

	const [cachedProfiles, setCachedProfiles] = useState({});
	const [profileData, setProfileData] = useState(null);
	const [publications, setPublications] = useState([]);
	const [userSession, setUserSession] = useState(null);
	const [lastFetchTime, setLastFetchTime] = useState({});

	// Función para obtener publicaciones (memoized)
	const fetchPublications = useCallback(async () => {
		if (!authCode) return;

		try {
			const url = `api/multimedia/user/get?authCode=${authCode}`;
			const data = await ApiService.fetchData(url);
			setPublications(data || []);
		} catch (error) {
			console.error("Error obteniendo publicaciones:", error);
		}
	}, [authCode]);

	// Fetch publications when authCode changes
	useEffect(() => {
		if (!authCode) return;
		fetchPublications();
	}, [authCode, fetchPublications]);

	// Fetch user session data once
	useEffect(() => {
		const fetchUserSession = async () => {
			try {
				const data = await ApiService.fetchData("api/user/mydata");
				setUserSession(data);
			} catch (error) {
				console.error("Error obteniendo usuario en sesión:", error);
			}
		};

		fetchUserSession();
	}, []);

	// Fetch user profile with caching
	useEffect(() => {
		if (!authCode) return;

		// Check if we already have cached data and if it's recent enough
		const now = Date.now();
		const cachedData = cachedProfiles[authCode];
		const lastFetch = lastFetchTime[authCode] || 0;
		const cacheTimeout = 5 * 60 * 1000; // 5 minutes cache

		// Use cached data if available and recent
		if (cachedData && now - lastFetch < cacheTimeout) {
			setProfileData(cachedData);
			return;
		}

		const fetchUserProfile = async () => {
			try {
				const data = await ApiService.fetchData(`api/user/profile?authCode=${authCode}`);
				if (data) {
					setProfileData(data);

					// Only cache if it's not the user's own profile
					if (!data.me) {
						setCachedProfiles((prev) => ({ ...prev, [authCode]: data }));
						setLastFetchTime((prev) => ({ ...prev, [authCode]: now }));
					}
				}
			} catch (error) {
				console.error("Error fetching user profile:", error);
			}
		};

		fetchUserProfile();
	}, [authCode]);

	const updateSliderImagesLocally = useCallback(
		(newSliderArray) => {
			setProfileData((prev) => {
				if (!prev || !prev.slider) return prev;
				return {
					...prev,
					slider: [
						{
							img1: newSliderArray[0],
							img2: newSliderArray[1],
							img3: newSliderArray[2],
						},
					],
				};
			});
		},
		[]
	);
	

	// Función para actualizar la imagen de perfil
	const updateProfileImage = useCallback(
		(newImageUrl) => {
			setProfileData((prev) => {
				if (!prev || !prev.user) return prev;
				return {
					...prev,
					user: {
						...prev.user,
						perfil: newImageUrl,
					},
				};
			});

			// Actualizar la caché
			setCachedProfiles((prevCache) => ({
				...prevCache,
				[authCode]: {
					...prevCache[authCode],
					user: {
						...prevCache[authCode]?.user,
						perfil: newImageUrl,
					},
				},
			}));

			// Actualizar userSession
			setUserSession((prev) => {
				if (!prev) return prev;
				return { ...prev, perfil: newImageUrl };
			});

			localStorage.setItem("profileImage", newImageUrl);

			// Actualizamos publicaciones (si es necesario)
			fetchPublications();
		},
		[authCode, fetchPublications]
	);

	const updateProfileLocally = useCallback(
		({ nombre, apellido, ...rest }) => {
		  /* 1) Actualizamos el profileData principal */
		  setProfileData(prev => {
			if (!prev || !prev.user) return prev;
	  
			const fullName =
			  `${nombre ?? prev.user.nombre ?? ""} ${apellido ?? prev.user.apellido ?? ""}`.trim();
	  
			return {
			  ...prev,
			  user: {
				...prev.user,
				...rest,   // birthey, localidad, descripcion…
				nombre,
				apellido,
				fullName,
			  },
			};
		  });
	  
		  /* 2) Refrescamos la caché si existe */
		  setCachedProfiles(prevCache => {
			const cacheItem = prevCache[authCode] ?? {};
			const cacheUser = cacheItem.user ?? {};
	  
			const fullNameCache =
			  `${nombre ?? cacheUser.nombre ?? ""} ${apellido ?? cacheUser.apellido ?? ""}`.trim();
	  
			return {
			  ...prevCache,
			  [authCode]: {
				...cacheItem,
				user: {
				  ...cacheUser,
				  ...rest,
				  nombre,
				  apellido,
				  fullName: fullNameCache,
				},
			  },
			};
		  });
		},
		[authCode]
	  );
	  
	  
	

	return (
		<UserContext.Provider
			value={{
				profileData,
				userSession,
				publications,
				fetchPublications,
				updateProfileImage,
				updateSliderImagesLocally,
				updateProfileLocally
			}}>
			{children}
		</UserContext.Provider>
	);
};
